import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { loadRemoteModule } from "@cw-elements/mfe-setup/load";
import { useConfig } from "@cw-elements/config";
import { useAppState } from "../../../lib/appContext/AppContext";
import { useTrialsRouter } from "../../../shared/custom-hooks/useTrialsRouter";

interface PrescriptionExporterProps {
  userLanguage: string;
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  agOperationIds: () => string[];
  orgCountry: string;
  getConfigInstance: () => unknown;
}

export interface PrescriptionExporterMfeLauncherProps {
  isOpen: boolean;
  onClose: () => void;
  agOperationIds: string[];
  userLanguage?: string;
  loadingFallback?: React.ReactNode;
  errorFallback?: React.ReactElement;
}

const PrescriptionExporter = React.lazy(async () => {
  const module = await loadRemoteModule<PrescriptionExporterProps>(
    "connect-files/prescription-exporter"
  );

  return { default: module.default };
});

/**
 * Renders the prescription exporter remote modal and injects org context and config.
 *
 * @param props Modal state, ag-operation ids, locale, and optional suspense/error fallbacks.
 * @returns The prescription exporter launcher component.
 */
function PrescriptionExporterMfeLauncher({
  isOpen,
  onClose,
  agOperationIds,
  userLanguage = "en",
  loadingFallback = "Loading MFE...",
  errorFallback = <span>MFE did not load</span>,
}: Readonly<PrescriptionExporterMfeLauncherProps>): React.JSX.Element {
  const { stateValue } = useAppState();
  const config = useConfig();
  const { orgId } = useTrialsRouter();
  const orgCountry =
    stateValue.orgsData?.content?.find((org: any) => org.id === orgId)
      ?.country ?? "US";

  return (
    <ErrorBoundary fallback={errorFallback}>
      <React.Suspense fallback={loadingFallback}>
        <PrescriptionExporter
          userLanguage={userLanguage}
          isOpen={isOpen}
          onClose={onClose}
          orgId={orgId ?? ""}
          agOperationIds={() => agOperationIds}
          orgCountry={orgCountry}
          getConfigInstance={() => config}
        />
      </React.Suspense>
    </ErrorBoundary>
  );
}

export default PrescriptionExporterMfeLauncher;
